import type { JSX } from "react";
import type {
  CalculationType,
  FieldConfiguration,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { DimensionField } from "../../../../../../../../../api/types/svTypes.ts";
import { isEligibleDateScopeField } from "../../../../../utilities/fieldsHelper.ts";
import CustomLabel from "../../../../viewer/common/fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../../../viewer/common/fields/FieldOptionsDivider.tsx";
import CustomId from "../../../api-report/editor/fields/CustomId.tsx";
import type { DateCalculationType } from "../../../common/fields/custom-field/utilities/dateCalculationTypes.ts";
import DimensionAggregation from "../../../common/fields/dimension-aggregation/DimensionAggregation.tsx";
import useDimensionFieldOptions from "../../../common/fields/dimensions/hooks/useDimensionFieldOptions.ts";
import CalculationFieldMenuItem from "../../../common/fields/field-options/CalculationFieldMenuItem.tsx";
import FreezeField from "../../../common/fields/field-options/FreezeField.tsx";
import SuppressBlankValues from "../../../common/fields/field-options/SuppressBlankValues.tsx";
import { isNumericField } from "../../../utils/isNumericField.ts";
import DateGroupingComponent from "../DateGroupingComponent.tsx";

interface Props {
  field: DimensionField;
  AggregationOptions: JSX.Element;
  isApiReport: boolean;
  canAddCustomField: boolean | undefined;
  readonly: boolean;
  cancel: () => void;
  onRemove: () => void;
  onUpdateConfiguration: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  onUpdateCustomLabel: (name: string | undefined) => void;
  onUpdateCustomId: (customId: string) => void;
  onCalculationItemClick: (calculationType: CalculationType) => void | undefined;
  onDateCalculationItemClick?: (type: DateCalculationType) => void;
}

export default function DimensionFieldOptions({
  field,
  AggregationOptions,
  cancel,
  onRemove,
  onUpdateConfiguration,
  onUpdateCustomLabel,
  isApiReport,
  onUpdateCustomId,
  canAddCustomField,
  onCalculationItemClick,
  onDateCalculationItemClick,
  readonly,
}: Props) {
  const {
    isReferencedDateField,
    isNumeric,
    aggregation,
    showGroupings,
    handleRemove,
    updateGrouping,
    saveChanges,
    onUpdateAggregation,
  } = useDimensionFieldOptions({
    field,
    cancel,
    onRemove,
    onUpdateConfiguration,
  });

  return (
    <>
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={onUpdateCustomLabel}
        readonly={readonly}
      />
      {isApiReport && (
        <CustomId
          customId={field.config.customId}
          defaultId={field.config.guid}
          updateCustomId={onUpdateCustomId}
          readonly={readonly}
        />
      )}
      <FieldOptionsDivider />
      {isNumericField(field) && canAddCustomField && !readonly && (
        <CalculationFieldMenuItem
          onCalculationItemClick={onCalculationItemClick}
          onDateCalculationItemClick={isEligibleDateScopeField(field) ? onDateCalculationItemClick : undefined}
        />
      )}
      <FieldOptionsDivider />
      {field.meta.options?.supportAggregation !== false && (
        <DimensionAggregation
          field={field}
          onChange={onUpdateAggregation}
          hideAggregationType={!!field.config.persistedAggregationName}
          readonly={readonly}
        />
      )}
      {showGroupings && (
        <DateGroupingComponent
          field={field}
          supportedDateGroupings={
            field.meta.options?.supportedColumnGrouping?.length
              ? field.meta.options?.supportedColumnGrouping
              : undefined
          }
          updateGrouping={updateGrouping}
          readonly={readonly}
        />
      )}
      {aggregation && AggregationOptions}
      {(aggregation || !isReferencedDateField) && (
        <SuppressBlankValues
          field={field}
          onChanged={saveChanges}
          showExcludeZeroValues={field.config.hideBlankValues === true && isNumeric}
          readonly={readonly}
        />
      )}
      <FreezeField field={field} readonly={readonly} />
      <FieldOptionsDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleRemove} />}
    </>
  );
}
